import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send } from 'lucide-react';
import { cn } from '../lib/cn';
import { type AttachmentDraft } from '../lib/media';

/**
 * Chat input — text-only surface (Batch 8a-2).
 *
 * Photo and voice-note attachment surfaces are deliberately hidden
 * until their backing pipelines exist end-to-end:
 *   - Photos render briefly via a local `blob:` URL but die on refresh;
 *     the LLM never sees the image (no R2 / S3 upload, no Anthropic
 *     vision round-trip).
 *   - Voice notes have no transcription pipeline and the chat-send
 *     gate skips voice-only messages outright, so they vanish silently.
 *
 * Premium-build mantra: don't expose UI that doesn't work end-to-end.
 * Re-enablement plan when the pipeline lands (deferred batch):
 *   1. Restore the Plus / Camera / Mic icon imports from lucide-react.
 *   2. Restore the menu state (`menuOpen`), refs (`menuRef`,
 *      `fileInputRef`), and the click-outside / Escape effect.
 *   3. Restore drafts state (`drafts`, `draftsRef`), the revoke-on-
 *      unmount effect, the `DraftChip` render block, and the helpers
 *      `removeDraft` / `handleFileSelected` / `handleVoiceComplete`.
 *   4. Restore the file `<input type="file">`, the Plus button +
 *      menu, and conditional render of `<VoiceRecorder>` while
 *      recording.
 *   5. Change `onSubmit` to pass `attachments: drafts` instead of
 *      always-empty.
 *
 * `ChatInputSubmission` retains the `attachments` field so MePage's
 * handler signature does not churn on hide / re-enable; today it
 * always receives an empty array. `VoiceRecorder.tsx` and the value
 * exports of `lib/media.ts` (processImageFile, MediaError, revokeDraft)
 * remain in the repo as unused modules — kept on disk for re-enablement
 * and tree-shaken out of the production bundle by Vite.
 *
 * Partner-aware send button (Batch 12): when `partnerPrimary` is
 * supplied, the send button bg picks up the partner brand colour via
 * inline style; hover uses `opacity-90` (can't compute a darker
 * Tailwind shade from an arbitrary hex without a colour library,
 * opacity is the clean substitute). Disabled state's `opacity-50`
 * still wins on the disabled selector. No-partner case unchanged
 * (bg-moor-700 / hover:bg-moor-800). Textarea and focus rings stay
 * moor as neutral input chrome — they're affordance, not partner
 * content.
 */

const schema = z.object({
  message: z.string().trim().max(2000, 'Message is too long'),
});
type FormValues = z.infer<typeof schema>;

export interface ChatInputSubmission {
  text: string;
  attachments: AttachmentDraft[];
}

interface ChatInputProps {
  onSend: (submission: ChatInputSubmission) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Partner primary hex (Batch 12). When present, the send button bg
   * picks up the partner brand colour. Undefined / absent → bg-moor-700
   * (the no-partner default). Optional + explicitly nullable to satisfy
   * exactOptionalPropertyTypes when MePage passes a `string | undefined`
   * value through.
   */
  partnerPrimary?: string | undefined;
}

export function ChatInput({
  onSend,
  disabled = false,
  placeholder = 'Message Morwenna…',
  partnerPrimary,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { message: '' },
  });

  const { ref: rhfRef, ...messageRest } = register('message');
  const value = watch('message');

  // Autoresize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [value]);

  const hasContent = !!(value && value.trim().length > 0);
  const canSend = !disabled && hasContent;

  const onSubmit = handleSubmit((data) => {
    if (!canSend) return;
    // Always-empty attachments — see file header. The handler signature
    // stays stable so MePage doesn't churn on hide / re-enable.
    onSend({ text: data.message.trim(), attachments: [] });
    reset({ message: '' });
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) void onSubmit();
    }
  };

  return (
    <div className="flex flex-col gap-2 px-3 py-2">
      <form onSubmit={onSubmit} className="flex items-end gap-2" aria-label="Chat input">
        <label htmlFor="chat-message" className="sr-only">
          Message
        </label>
        <textarea
          id="chat-message"
          rows={1}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? 'chat-message-error' : undefined}
          className={cn(
            'min-h-[44px] flex-1 resize-none rounded-2xl border border-moor-200 bg-cream-50 px-4 py-2.5',
            'text-[15px] text-moor-900 placeholder:text-moor-400',
            'focus:border-moor-500 focus:outline-none focus:ring-2 focus:ring-moor-500/30',
            'disabled:opacity-60'
          )}
          onKeyDown={handleKeyDown}
          {...messageRest}
          ref={(el) => {
            rhfRef(el);
            textareaRef.current = el;
          }}
        />

        <button
          type="submit"
          disabled={!canSend}
          aria-label="Send message"
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
            'text-cream-50 transition-colors',
            partnerPrimary
              ? 'hover:opacity-90'
              : 'bg-moor-700 hover:bg-moor-800',
            'focus:outline-none focus:ring-2 focus:ring-moor-500/40 focus:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
          style={
            partnerPrimary ? { backgroundColor: partnerPrimary } : undefined
          }
        >
          <Send className="h-5 w-5" aria-hidden="true" />
        </button>
      </form>

      {errors.message?.message && (
        <p id="chat-message-error" role="alert" className="px-1 text-xs text-red-700">
          {errors.message.message}
        </p>
      )}
    </div>
  );
}
